import { useMutationDeleteUser, usersQueryKey } from "@apis:user/user";
import { ConfirmationDialog } from "@components:ConfirmationDialog";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useToasterStoreActions } from "@stores:global";
import { useTranslation } from "react-i18next";

interface UserDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

export const UserDeleteModal = ({
  isOpen,
  onClose,
  userId,
}: UserDeleteModalProps) => {
  const { t } = useTranslation();
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();
  const mutationDeleteUser = useMutationDeleteUser({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        message: t("tenant.users.deleted"),
        variant: "success",
      });
      await invalidateQueries.run([
        {
          queryKey: usersQueryKey._def,
        },
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: t("tenant.users.deleteFailed"),
        variant: "error",
      });
    },
  });

  const handleDelete = async () => {
    await mutationDeleteUser.mutateAsync({ id: userId });
  };

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={() => onClose()}
      title={t("tenant.users.deleteTitle")}
      description={t("tenant.users.deleteConfirm")}
      actionControls={{
        secondary: {
          label: t("common.cancel"),
          onClick: () => {
            onClose();
          },
          sx: {
            width: "5rem",
          },
        },
        primary: {
          sx: {
            width: "5rem",
            backgroundColor: (theme) => theme.palette.error.main,
          },
          label: t("common.delete"),
          onClick: async () => {
            await handleDelete();
          },
          disabled: !userId,
        },
      }}
    />
  );
};
